import { CordovaPlugins } from '../../shared/CordovaPlugins';
import { isWeb, isWindows } from '../../shared/utils/is';
import { clearIndexedDb, zenputDb } from '../../shared/utils/storage/db';
import { getDocument, getKeys, removeDocument, saveDocument } from '../../shared/utils/storage/legacy_data';

// This is borrowed from the Native Storage module
const ITEM_NOT_FOUND = 2;
const KEYS_KEY = '__zenput_storage_keys'; // the storage key we will use on mobile to keep track of our keys. @see this.keys()

// SQLite is only used for Windows
const SQLITE_DB_NAME = 'zenput.db';
const SQLITE_TABLE_NAME = 'zenput_keyvalues';

const LOCAL_STORAGE_SYSTEM = 'local-storage';
const NATIVE_STORAGE_SYSTEM = 'native-storage';
const SQLITE_STORAGE_SYSTEM = 'sqlite';
const INDEXEDDB_STORAGE_SYSTEM = 'indexeddb';

// NativeStorage catches all unhandled exceptions and calls the error callback. By default,
// we should re-raise these exceptions so they are evident.
const DEFAULT_ERROR_CALLBACK = (error) => {
    if (!error?.exception) {
        return;
    }

    throw error.exception;
};

/**
 * Unfortunately, NativeStorage wraps its success() callbacks inside a try/catch, and then calls error(),
 * swallowing the error. To avoid this, we'll do that ourselves and catch/log any errors so NativeStorage
 * never swallows anything.
 *
 * @param {function} fn The function to wrap in try/catch
 * @return {function} The function passed in, but surrounded by a try/catch
 */
const wrapTryCatch = (fn) => {
    return function () {
        try {
            fn.apply(this, arguments);
        } catch (e) {
            console.error(e);
            $f.mobile.logging.capture_exception(e);
            throw e; // for now, re-throw the error in case something NativeStorage was doing is actually saving us
        }
    };
};

/**
 * Because NativeStorage does not have a built-in keys() function to get all keys we have saved,
 * we need to manage them ourselves.
 *
 * @note this is only for native Android/iOS
 */
const registerKeyInCache = (new_key) =>
    new Promise((resolve, reject) => {
        storage.keys((keys) => {
            if (keys.includes(new_key)) {
                resolve(); // already accounted for!
                return;
            }

            // add the new key and re-save our keys list (both in memory and on disk)
            const newKeys = [...keys, new_key];
            storage.keys_cache = newKeys;
            storage.set(KEYS_KEY, newKeys, resolve, reject);
        }, reject);
    });

const unregisterKeyFromCache = (keyToRemove) =>
    new Promise((resolve, reject) => {
        storage.get(
            KEYS_KEY,
            (keys = []) => {
                if (!keys.length) {
                    resolve();
                    return;
                }

                const newKeys = keys.filter((key) => key === keyToRemove);

                if (keys.length === newKeys.length) {
                    resolve();
                    return;
                }

                storage.keys_cache = newKeys; // update cache!
                storage.set(KEYS_KEY, newKeys, resolve, reject);
            },
            reject
        );
    });

const initSQL = () => {
    const db = CordovaPlugins().sqlitePlugin.openDatabase({
        name: SQLITE_DB_NAME,
        location: 'default'
    });

    return new Promise((resolve) => {
        // Create table
        db.transaction((tr) => {
            tr.executeSql(
                `CREATE TABLE IF NOT EXISTS ${SQLITE_TABLE_NAME} (\`key\` VARCHAR(45) NOT NULL UNIQUE, \`value\` BLOB NULL);`,
                [],
                function (_tr, response) {
                    resolve(db);
                },
                function (error) {
                    $f.mobile.log('Error initializing SQLite', error);
                    resolve(db);
                }
            );
        });
    });
};

const writeKeyValToNativeStorage = (key, value) => {
    return new Promise((resolve, reject) => {
        CordovaPlugins().NativeStorage.setItem(
            key,
            value,
            async () => {
                // after successfully setting an item in storage, we need to take note of its key
                // for future reference. @see this.keys()
                await registerKeyInCache(key);
                resolve();
            },
            reject
        );
    });
};

const writeKeyValToSQL = (key, value) => {
    return new Promise((resolve, reject) => {
        storage.sqlite_db.transaction(
            (tr) => {
                try {
                    value = JSON.stringify(value);
                } catch (e) {
                    // value can't be stringified
                }

                tr.executeSql(
                    `INSERT OR REPLACE INTO ${SQLITE_TABLE_NAME} (\`key\`, \`value\`) VALUES (?, ?);`,
                    [key, value],
                    function (_tr, response) {
                        resolve();
                    }
                );
            },
            function (err) {
                reject(err);
            }
        );
    });
};

const writeKeyValToLocalStorage = (key, value) => {
    return new Promise((resolve, reject) => {
        if (value === undefined) {
            resolve();
            return;
        }

        try {
            value = JSON.stringify(value);
        } catch (e) {
            // value can't be stringified
        }

        try {
            localStorage.setItem(key, value);
        } catch (e) {
            reject(e.message || 'Failed to save to localStorage');
            return;
        }

        resolve();
    });
};

const readKeyValueFromNativeStorage = (key) =>
    new Promise((resolve, reject) => {
        CordovaPlugins().NativeStorage.getItem(key, resolve, (err) => {
            if (err?.code === ITEM_NOT_FOUND) {
                resolve(null);
            } else {
                reject();
            }
        });
    });

const readKeyValueFromSQL = (key) => {
    return new Promise((resolve, reject) => {
        storage.sqlite_db.transaction(
            (tr) => {
                tr.executeSql(`SELECT value FROM ${SQLITE_TABLE_NAME} WHERE \`key\` = ?`, [key], (_tr, response) => {
                    if (
                        !response.rows.length ||
                        typeof response.rows.item(0) === 'undefined' ||
                        response.rows.item(0) === null
                    ) {
                        return resolve(null);
                    }

                    var value = response.rows.item(0).value;
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        // value can't be parsed
                    }

                    resolve(value);
                });
            },
            function (err) {
                reject(err);
            }
        );
    });
};

const readKeyValueFromLocalStorage = (key) => {
    const value = localStorage.getItem(key);

    return new Promise((resolve) => {
        try {
            const parsedValue = JSON.parse(value);
            resolve(parsedValue);
            return;
        } catch (e) {
            // value can't be parsed
        }

        // if val is the word "undefined", return actual undefined. Everything else would have been caught by JSON.parse()
        if (value === 'undefined') {
            resolve(undefined);
            return;
        }

        resolve(value);
    });
};

const removeKeyValueFromNativeStorage = (key) =>
    new Promise((resolve, reject) => {
        CordovaPlugins().NativeStorage.remove(
            key,
            async () => {
                await unregisterKeyFromCache(key);
                resolve();
            },
            reject
        );
    });

const removeKeyValueFromSQL = (key) => {
    return new Promise((resolve, reject) => {
        storage.sqlite_db.transaction(
            (tr) => {
                tr.executeSql(`DELETE FROM ${SQLITE_TABLE_NAME} WHERE \`key\` = ?;`, [key], (_tr, response) => {
                    resolve();
                });
            },
            function (_error) {
                reject();
            }
        );
    });
};

const removeKeyValueFromLocalStorage = (key) =>
    new Promise((resolve) => {
        localStorage.removeItem(key);
        resolve();
    });

const clearNativeStorage = () =>
    new Promise((resolve, reject) => {
        const newKeys = [];

        storage.keys_cache = newKeys;
        storage.set(
            KEYS_KEY,
            newKeys,
            () => {
                CordovaPlugins().NativeStorage.clear(resolve, reject);
            },
            reject
        );
    });

const clearSQL = () => {
    return new Promise((resolve, reject) => {
        storage.sqlite_db.transaction(
            (tr) => {
                tr.executeSql(`DELETE FROM ${SQLITE_TABLE_NAME};`, [], (_tr, response) => {
                    resolve();
                });
            },
            (_error) => {
                reject(_error);
            }
        );
    });
};

const clearLocalStorage = () =>
    new Promise((resolve) => {
        localStorage.clear();
        resolve();
    });

const getKeysFromNativeStorage = () =>
    new Promise((resolve) => {
        if (storage.keys_cache) {
            resolve(storage.keys_cache);
            return;
        }

        CordovaPlugins().NativeStorage.getItem(
            KEYS_KEY,
            (keys = []) => {
                storage.keys_cache = keys; // update our JS cache
                resolve(keys);
            },
            () => {
                // if there is no keys list, create one!
                storage.keys_cache = [];
                resolve(storage.keys_cache);
            }
        );
    });

const getKeysFromSQL = () => {
    return new Promise((resolve, reject) => {
        storage.sqlite_db.transaction(
            (tr) => {
                tr.executeSql(`SELECT \`key\` FROM ${SQLITE_TABLE_NAME};`, [], (_tr, response) => {
                    var keys = [];
                    for (var i = 0; i < response.rows.length; i++) {
                        keys.push(response.rows.item(i).key);
                    }

                    resolve(keys);
                });
            },
            (_error) => {
                reject(_error);
            }
        );
        return;
    });
};

const getKeysFromLocalStorage = () =>
    new Promise((resolve) => {
        resolve(Object.keys(localStorage));
    });

const keyValueStorageCRUD = {
    // Remove when PWA is ready ZP-XXXX
    [LOCAL_STORAGE_SYSTEM]: {
        clear: clearLocalStorage,
        get: readKeyValueFromLocalStorage,
        getKeys: getKeysFromLocalStorage,
        remove: removeKeyValueFromLocalStorage,
        set: writeKeyValToLocalStorage
    },
    [NATIVE_STORAGE_SYSTEM]: {
        clear: clearNativeStorage,
        get: readKeyValueFromNativeStorage,
        getKeys: getKeysFromNativeStorage,
        remove: removeKeyValueFromNativeStorage,
        set: writeKeyValToNativeStorage
    },
    [SQLITE_STORAGE_SYSTEM]: {
        clear: clearSQL,
        get: readKeyValueFromSQL,
        getKeys: getKeysFromSQL,
        remove: removeKeyValueFromSQL,
        set: writeKeyValToSQL
    },
    [INDEXEDDB_STORAGE_SYSTEM]: {
        clear: clearIndexedDb,
        get: (key) => getDocument(key),
        getKeys: () => getKeys(),
        remove: (key) => removeDocument(key),
        set: (key, value) => saveDocument(key, value)
    }
};

const getPlatformStorageSystem = () => {
    if (isWeb()) {
        const metaTag = document.querySelector('meta[name="use-rxdb"]');
        const useIndexedDB = metaTag ? metaTag.getAttribute('content') === 'true' : false;
        if (useIndexedDB && isWeb()) {
            return INDEXEDDB_STORAGE_SYSTEM;
        } else {
            return LOCAL_STORAGE_SYSTEM;
        }
    }

    if (isWindows()) {
        return SQLITE_STORAGE_SYSTEM;
    }

    return NATIVE_STORAGE_SYSTEM;
};

const getPlatformCRUDMethods = () => keyValueStorageCRUD[getPlatformStorageSystem()];

const storage = {
    keys_cache: null, // [] - in-memory cache of storage keys so we don't have to keep getting from disk
    sqlite_db: null, // SQLite Database used for Windows
    indexed_db: null, // IndexedDB used for PWA

    initialize: async function () {
        if (getPlatformStorageSystem() === SQLITE_STORAGE_SYSTEM) {
            this.sqlite_db = await initSQL();
        }

        if (getPlatformStorageSystem() === INDEXEDDB_STORAGE_SYSTEM) {
            this.indexed_db = zenputDb;
        }
    },

    set: function (key, value, success = () => {}, error = DEFAULT_ERROR_CALLBACK) {
        success = wrapTryCatch(success);

        getPlatformCRUDMethods().set(key, value).then(success).catch(error);
    },

    get: function (key, success = () => {}, error = DEFAULT_ERROR_CALLBACK) {
        success = wrapTryCatch(success);

        getPlatformCRUDMethods().get(key).then(success).catch(error);
    },

    remove: function (key, success = () => {}, error = DEFAULT_ERROR_CALLBACK) {
        success = wrapTryCatch(success);

        getPlatformCRUDMethods().remove(key).then(success).catch(error);
    },

    clear: function (success = () => {}, error = DEFAULT_ERROR_CALLBACK) {
        success = wrapTryCatch(success);

        getPlatformCRUDMethods().clear().then(success).catch(error);
    },

    /**
     * Because NativeStorage doesn't offer any way to track the keys that are saved in local storage,
     * we have to do so ourselves.
     */
    keys: function (success = () => {}, error = () => {}) {
        keyValueStorageCRUD[getPlatformStorageSystem()].getKeys().then(success).catch(error);
    }
};

export default storage;
